import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AppContainer from '../Layout/AppContainer';
import ProgressBar from '../Navigation/ProgressBar';
import ScreenRenderer from '../Screens/ScreenRenderer';
import {
  createUserSession,
  getUserSession,
  updateUserProgress,
  getScreenData,
  getCheckpointData,
  getWarmCheckpointData,
  getNextScreen,
  isExternalUrl,
  saveCheckpointResponse,
  saveJournalEntry,
  calculateSectionProgress,
  buildOptimisticUserSession,
  generateShortcode
} from '../../services/funnelService';
import { retryFailedActiveCampaignSyncs } from '../../services/activeCampaignRetryService';
import {
  getShortcodeFromStorage,
  saveUserSessionToStorage,
  saveCurrentPathToStorage,
  getLastPathFromStorage,
  isStandaloneMode
} from '../../services/browserStorageService';
import { maybeSendConversionEvents } from '../../services/conversionService';
import { AD_ENTRY_CHECKPOINT_KEYS } from '../../data/adEntrySnapshots';
import { COLD_PATH_TIMEOUT_MS, withTimeout } from '../../utils/withTimeout';

const FunnelContainer = () => {
  const { shortcode, screenKey } = useParams(); // Changed from screenName to screenKey
  const navigate = useNavigate();
  const lastLoadConversionKey = useRef(null);
  const isNavigatingRef = useRef(false);
  const location = useLocation();

  const resolveUrlScreenKey = () => {
    if (screenKey) return screenKey;
    if (shortcode && !/^[A-Z0-9]{6}$/.test(shortcode)) return shortcode;
    return null;
  };

  const initialScreenKey = resolveUrlScreenKey();
  const initialWarmCheckpoint =
    initialScreenKey && AD_ENTRY_CHECKPOINT_KEYS.has(initialScreenKey)
      ? getWarmCheckpointData(initialScreenKey)
      : null;
  
  // State management
  const [userSession, setUserSession] = useState(() => {
    if (shortcode && /^[A-Z0-9]{6}$/.test(shortcode)) {
      return buildOptimisticUserSession(shortcode);
    }
    return null;
  });
  const [currentScreenData, setCurrentScreenData] = useState(null);
  const [currentCheckpointData, setCurrentCheckpointData] = useState(initialWarmCheckpoint);
  const [isLoading, setIsLoading] = useState(!initialWarmCheckpoint);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Initialize or load user session
  const initializeSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if app was opened from home screen (standalone mode) and restore last path
      if (isStandaloneMode() && window.location.pathname === '/optimalpace') {
        const lastPath = getLastPathFromStorage();
        if (lastPath && lastPath !== '/optimalpace' && lastPath.startsWith('/optimalpace')) {
          // Restore the last visited deep URL
          navigate(lastPath, { replace: true });
          return;
        }
      }
      
      let session;
      let effectiveShortcode = shortcode;
      let actualScreenKey = screenKey;
      
      // Detect if we're in direct screen access mode
      // Shortcodes are exactly 6 characters and alphanumeric only
      // Screen keys can be any length and contain various characters
      if (shortcode && !screenKey) {
        const isLikelyShortcode = /^[A-Z0-9]{6}$/.test(shortcode);
        if (!isLikelyShortcode) {
          // This is likely a screenKey, not a shortcode
          actualScreenKey = shortcode;
          effectiveShortcode = null;
        }
      }

      // Warm-paint ad-entry checkpoints immediately (never wait on Firestore for first impression)
      if (actualScreenKey && AD_ENTRY_CHECKPOINT_KEYS.has(actualScreenKey)) {
        const warm = getWarmCheckpointData(actualScreenKey);
        if (warm) {
          setCurrentCheckpointData(warm);
          setCurrentScreenData(null);
        }
      }
      
      if (effectiveShortcode) {
        // Immediate optimistic session so first paint + taps never wait on Firestore
        const optimistic = buildOptimisticUserSession(effectiveShortcode);
        setUserSession(optimistic);
        saveUserSessionToStorage(optimistic);
        setIsLoading(false);

        // URL has shortcode — hydrate or create with hard timeout (fail-open for Instagram WebViews)
        try {
          session = await withTimeout(
            getUserSession(effectiveShortcode),
            COLD_PATH_TIMEOUT_MS,
            'getUserSession'
          );

          if (!session) {
            session = await withTimeout(
              createUserSession(effectiveShortcode),
              COLD_PATH_TIMEOUT_MS,
              'createUserSession'
            );
          }
        } catch (coldErr) {
          console.warn('Cold-path session hydrate failed/timed out; keeping optimistic session', coldErr);
          session = optimistic;
          // Background retry — do not block first paint
          createUserSession(effectiveShortcode)
            .then((created) => {
              setUserSession(created);
              saveUserSessionToStorage(created);
            })
            .catch((err) => {
              console.error('Background createUserSession failed:', err);
            });
        }

        saveUserSessionToStorage(session);
        
      } else {
        // No shortcode in URL - check browser storage for existing user
        const storedShortcode = getShortcodeFromStorage();
        
        if (storedShortcode) {
          try {
            session = await withTimeout(
              getUserSession(storedShortcode),
              COLD_PATH_TIMEOUT_MS,
              'getUserSession(stored)'
            );
          } catch (coldErr) {
            console.warn('Stored session fetch timed out; continuing with local shortcode', coldErr);
            session = null;
          }
          
          if (session) {
            // Found existing session, redirect to URL with shortcode
            const targetPath = actualScreenKey 
              ? `/optimalpace/${storedShortcode}/${actualScreenKey}`
              : `/optimalpace/${storedShortcode}`;
            saveCurrentPathToStorage(targetPath);
            navigate({ pathname: targetPath, search: location.search }, { replace: true });
            return;
          } else {
            // Stored shortcode doesn't exist / timed out — create or optimistic
            try {
              session = await withTimeout(
                createUserSession(storedShortcode),
                COLD_PATH_TIMEOUT_MS,
                'createUserSession(stored)'
              );
            } catch (_) {
              session = buildOptimisticUserSession(storedShortcode);
              createUserSession(storedShortcode).catch(() => {});
            }
          }
        } else {
          // No stored session — generate shortcode sync, create with timeout
          const newShortcode = generateShortcode();
          try {
            session = await withTimeout(
              createUserSession(newShortcode),
              COLD_PATH_TIMEOUT_MS,
              'createUserSession(new)'
            );
          } catch (_) {
            session = buildOptimisticUserSession(newShortcode);
            createUserSession(newShortcode).catch(() => {});
          }
        }
        
        // Save new session to browser storage
        saveUserSessionToStorage(session);
        
        // Redirect to URL with the shortcode
        const targetPath = actualScreenKey 
          ? `/optimalpace/${session.shortcode}/${actualScreenKey}`
          : `/optimalpace/${session.shortcode}`;
        saveCurrentPathToStorage(targetPath);
        navigate({ pathname: targetPath, search: location.search }, { replace: true });
        return;
      }
      
      setUserSession(session);
      
      // Retry any failed ActiveCampaign syncs when user returns (non-blocking)
      if (session.pendingActiveCampaignSync && session.pendingActiveCampaignSync.length > 0) {
        // Fire and forget - don't block screen loading on ActiveCampaign syncs
        retryFailedActiveCampaignSyncs(session.shortcode)
          .then(() => {
            // Refresh session data to get updated sync status (non-blocking)
            getUserSession(session.shortcode)
              .then(updatedSession => {
                if (updatedSession) {
                  setUserSession(updatedSession);
                  saveUserSessionToStorage(updatedSession);
                }
              })
              .catch(err => {
                console.error('Failed to refresh session after retry:', err);
              });
          })
          .catch(error => {
            console.error('Failed to retry ActiveCampaign syncs:', error);
            // Continue with funnel loading even if retry fails
          });
      }
      
      // Only load screen data if there's no specific screenKey in URL
      // The separate useEffect will handle loading when screenKey is present
      if (!actualScreenKey) {
        let targetScreenKey = session.currentScreen || 'welcome'; // Default to welcome screen
        await loadScreenData(targetScreenKey);
      }
      
    } catch (err) {
      console.error('Error initializing session:', err);
      setError('Failed to load funnel. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [shortcode, screenKey, navigate, location.search]);

  // Load screen or checkpoint data by key
  const loadScreenData = async (screenKey, forceRefresh = false) => {
    if (!screenKey) {
      setCurrentScreenData(null);
      setCurrentCheckpointData(null);
      return Promise.resolve();
    }

    try {
      // Warm paint for ad-entry keys before network
      if (AD_ENTRY_CHECKPOINT_KEYS.has(screenKey)) {
        const warm = getWarmCheckpointData(screenKey);
        if (warm) {
          setCurrentCheckpointData(warm);
          setCurrentScreenData(null);
        }
      }

      // Preload the new data without clearing current data
      let newScreenData = null;
      let newCheckpointData = null;
      
      // Try to load as screen first
      newScreenData = await getScreenData(screenKey);
      
      if (!newScreenData) {
        // Try to load as checkpoint (uses cache/snapshot fallback internally)
        newCheckpointData = await getCheckpointData(screenKey);
      }
      
      // Only update state when we have successfully loaded new data
      // This prevents any intermediate empty states that cause flashing
      if (newScreenData) {
        // Update to new screen data - previous screen stays visible until this completes
        setCurrentScreenData(newScreenData);
        setCurrentCheckpointData(null);
        setError(null);
      } else if (newCheckpointData) {
        // Update to new checkpoint data - previous screen stays visible until this completes
        setCurrentScreenData(null);
        setCurrentCheckpointData(newCheckpointData);
        setError(null);
      } else {
        // Neither found - only now do we clear and show error
        console.error(`Screen/checkpoint not found: ${screenKey}`);
        setCurrentScreenData(null);
        setCurrentCheckpointData(null);
        setError(`Screen "${screenKey}" not found`);
      }
      
    } catch (err) {
      console.error('Error loading screen data:', err);
      // Keep warm/cached paint if we already have it for this key
      if (!(AD_ENTRY_CHECKPOINT_KEYS.has(screenKey) && getWarmCheckpointData(screenKey))) {
        setCurrentScreenData(null);
        setCurrentCheckpointData(null);
        setError('Failed to load screen data');
      }
    }
  };

  // Handle navigation to next screen
  const handleNext = async (currentScreenKey, userResponse = null) => {
    if (!userSession) return;
    // Prevent double-submit (double-click / overlapping onNext + onJournalEntry, etc.)
    if (isNavigatingRef.current) {
      console.log(`[Conversion] handleNext ignored — navigation already in progress for "${currentScreenKey}"`);
      return;
    }
    isNavigatingRef.current = true;
    
    try {
      const currentData = currentScreenData || currentCheckpointData;
      
      // Handle membership_entry screen data saving
      if (userResponse && userResponse.type === 'membership_entry' && userResponse.formData) {
        try {
          const currentUserInfo = userSession.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update all provided fields
          if (userResponse.formData.name) {
            updatedUserInfo.name = userResponse.formData.name;
          }
          if (userResponse.formData.email) {
            updatedUserInfo.email = userResponse.formData.email;
          }
          if (userResponse.formData.phone) {
            updatedUserInfo.phone = userResponse.formData.phone;
          }
          if (userResponse.formData.countryCode) {
            updatedUserInfo.countryCode = userResponse.formData.countryCode;
          }
          if (userResponse.formData.location) {
            updatedUserInfo.location = userResponse.formData.location;
          }
          if (userResponse.formData.profession) {
            updatedUserInfo.profession = userResponse.formData.profession;
          }
          
          // Persist userInfo changes to Firebase
          await updateUserProgress(userSession.shortcode, { userInfo: updatedUserInfo });
          
          // Update local session state
          setUserSession(prev => {
            const updatedSession = {
              ...prev,
              userInfo: updatedUserInfo
            };
            // Save updated session to browser storage
            saveUserSessionToStorage(updatedSession);
            return updatedSession;
          });
        } catch (error) {
          console.error('Failed to persist membership_entry userInfo to Firebase:', error);
          // Continue with navigation even if saving fails
        }
      }
      
      // Handle custom navigation actions
      let nextDestination;
      if (userResponse && userResponse.action === 'navigate_to_screen' && userResponse.screenKey) {
        nextDestination = userResponse.screenKey;
      } else {
        nextDestination = getNextScreen(currentData, userResponse);
      }

      const nextIsExternalUrl = isExternalUrl(nextDestination);
      
      // Update user progress
      const completedScreens = [...userSession.completedScreens];
      if (currentScreenKey && !completedScreens.includes(currentScreenKey)) {
        completedScreens.push(currentScreenKey);
      }
      
      const updates = {
        // Don't store external URLs as currentScreen — mark funnel complete instead
        currentScreen: nextIsExternalUrl ? null : nextDestination,
        completedScreens,
        ...(nextIsExternalUrl || !nextDestination ? { completedFunnel: true } : {})
      };
      
      await updateUserProgress(userSession.shortcode, updates);
      
      // Fire ad conversion events for completed screens (when trigger is 'complete')
      if (currentScreenData?.key === currentScreenKey) {
        maybeSendConversionEvents({
          shortcode: userSession.shortcode,
          itemData: currentScreenData,
          itemType: 'screen',
          response: userResponse,
          trigger: 'complete'
        });
      }
      
      // Update local state
      setUserSession(prev => {
        const updatedSession = { ...prev, ...updates };
        // Save updated session to browser storage
        saveUserSessionToStorage(updatedSession);
        return updatedSession;
      });
      
      if (nextIsExternalUrl) {
        window.location.assign(nextDestination.trim());
      } else if (nextDestination) {
        // Preload the next screen data before navigating to prevent flashing
        await loadScreenData(nextDestination);
        
        // Now navigate to next screen - data should already be loaded
        const nextPath = `/optimalpace/${userSession.shortcode}/${nextDestination}`;
        saveCurrentPathToStorage(nextPath);
        navigate(nextPath);
      } else {
        // End of funnel
        const completePath = `/optimalpace/${userSession.shortcode}/complete`;
        saveCurrentPathToStorage(completePath);
        navigate(completePath);
      }
      
    } catch (err) {
      console.error('Error navigating to next screen:', err);
      setError('Failed to proceed to next screen');
    } finally {
      isNavigatingRef.current = false;
    }
  };

  // Handle checkpoint responses with conditional navigation
  const handleCheckpointResponse = async (response) => {
    if (!userSession) return;
    
    try {
      // Update response to use checkpoint key
      const responseWithKey = {
        ...response,
        checkpointKey: currentCheckpointData.key
      };
      
      await saveCheckpointResponse(userSession.shortcode, responseWithKey);
      
      // Update local session state
      setUserSession(prev => {
        const updatedSession = {
          ...prev,
          responses: {
            ...prev.responses,
            [responseWithKey.checkpointKey]: responseWithKey
          }
        };
        
        // For email_capture checkpoints, also update userInfo in local state
        if (responseWithKey.type === 'email_capture' && responseWithKey.formData) {
          const currentUserInfo = prev.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update email and name if provided
          if (responseWithKey.formData.email) {
            updatedUserInfo.email = responseWithKey.formData.email;
          }
          if (responseWithKey.formData.name) {
            updatedUserInfo.name = responseWithKey.formData.name;
          }
          
          updatedSession.userInfo = updatedUserInfo;
        }
        
        // For phone_verification checkpoints, also update userInfo in local state
        if (responseWithKey.type === 'phone_verification' && responseWithKey.formData) {
          const currentUserInfo = prev.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update name, phone, and email if provided
          if (responseWithKey.formData.name) {
            updatedUserInfo.name = responseWithKey.formData.name;
          }
          if (responseWithKey.formData.phone) {
            updatedUserInfo.phone = responseWithKey.formData.phone;
          }
          if (responseWithKey.formData.email) {
            updatedUserInfo.email = responseWithKey.formData.email;
          }
          
          // For phone verification, also save country code
          if (responseWithKey.formData.countryCode) {
            updatedUserInfo.countryCode = responseWithKey.formData.countryCode;
          }
          
          updatedSession.userInfo = updatedUserInfo;
        }
        
        // For membership_entry screens, also update userInfo in local state
        if (responseWithKey.type === 'membership_entry' && responseWithKey.formData) {
          const currentUserInfo = prev.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update all provided fields
          if (responseWithKey.formData.name) {
            updatedUserInfo.name = responseWithKey.formData.name;
          }
          if (responseWithKey.formData.email) {
            updatedUserInfo.email = responseWithKey.formData.email;
          }
          if (responseWithKey.formData.phone) {
            updatedUserInfo.phone = responseWithKey.formData.phone;
          }
          if (responseWithKey.formData.countryCode) {
            updatedUserInfo.countryCode = responseWithKey.formData.countryCode;
          }
          if (responseWithKey.formData.location) {
            updatedUserInfo.location = responseWithKey.formData.location;
          }
          if (responseWithKey.formData.profession) {
            updatedUserInfo.profession = responseWithKey.formData.profession;
          }
          
          updatedSession.userInfo = updatedUserInfo;
        }

        // For goals checkpoints, also update userInfo.goals in local state
        if (responseWithKey.type === 'goals' && Array.isArray(responseWithKey.topGoals)) {
          const currentUserInfo = prev.userInfo || {};
          const goalsMap = {};
          responseWithKey.topGoals.forEach((goal, index) => {
            if (typeof goal === 'string' && goal.trim()) {
              goalsMap[String(index + 1)] = goal.trim();
            }
          });
          const nextUserInfo = {
            ...currentUserInfo,
            goals: goalsMap,
          };
          if (
            typeof responseWithKey.timeframeMonths === 'number' &&
            Number.isFinite(responseWithKey.timeframeMonths)
          ) {
            nextUserInfo.goalTimeframeMonths = responseWithKey.timeframeMonths;
          }
          updatedSession.userInfo = nextUserInfo;
        }

        const toLabelStrings = (items) => {
          if (!Array.isArray(items)) return [];
          return items
            .map((item) => {
              if (typeof item === 'string') return item.trim();
              if (item && typeof item.label === 'string') return item.label.trim();
              return '';
            })
            .filter(Boolean);
        };

        // For speedbumps checkpoints, also update userInfo in local state
        if (responseWithKey.type === 'speedbumps') {
          const currentUserInfo = prev.userInfo || {};
          updatedSession.userInfo = {
            ...currentUserInfo,
            challengeChips: toLabelStrings(responseWithKey.challengeChips),
            desiredChips: toLabelStrings(responseWithKey.desiredChips),
            whereYouAre:
              typeof responseWithKey.whereYouAre === 'string'
                ? responseWithKey.whereYouAre
                : '',
            lookingForward:
              typeof responseWithKey.lookingForward === 'string'
                ? responseWithKey.lookingForward
                : '',
            speedbumps: toLabelStrings(responseWithKey.speedbumps),
            warningsigns: toLabelStrings(responseWithKey.warningSigns),
          };
        }

        // For which_are_you checkpoints, also update userInfo.who in local state
        if (
          responseWithKey.type === 'which_are_you' &&
          Array.isArray(responseWithKey.roles)
        ) {
          const currentUserInfo = prev.userInfo || {};
          updatedSession.userInfo = {
            ...currentUserInfo,
            who: toLabelStrings(responseWithKey.roles),
          };
        }

        // For first_name checkpoints, also update userInfo.name in local state
        if (responseWithKey.type === 'first_name' && responseWithKey.formData?.name) {
          const currentUserInfo = prev.userInfo || {};
          updatedSession.userInfo = {
            ...currentUserInfo,
            name: String(responseWithKey.formData.name).trim(),
          };
        }
        
        // Save updated session to browser storage
        saveUserSessionToStorage(updatedSession);
        return updatedSession;
      });
      
      // For email_capture and phone_verification checkpoints, also persist userInfo changes to Firebase
      if (responseWithKey.type === 'email_capture' && responseWithKey.formData) {
        try {
          const currentUserInfo = userSession.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update email and name if provided
          if (responseWithKey.formData.email) {
            updatedUserInfo.email = responseWithKey.formData.email;
          }
          if (responseWithKey.formData.name) {
            updatedUserInfo.name = responseWithKey.formData.name;
          }
          
          // Persist userInfo changes to Firebase
          await updateUserProgress(userSession.shortcode, { userInfo: updatedUserInfo });
        } catch (error) {
          console.error(`Failed to persist userInfo to Firebase for ${responseWithKey.type}:`, error);
        }
      }
      
      // For phone_verification checkpoints, also persist userInfo changes to Firebase
      if (responseWithKey.type === 'phone_verification' && responseWithKey.formData) {
        try {
          const currentUserInfo = userSession.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update name, email, and phone if provided
          if (responseWithKey.formData.name) {
            updatedUserInfo.name = responseWithKey.formData.name;
          }
          if (responseWithKey.formData.email) {
            updatedUserInfo.email = responseWithKey.formData.email;
          }
          if (responseWithKey.formData.phone) {
            updatedUserInfo.phone = responseWithKey.formData.phone;
          }
          
          // For phone verification, also save country code
          if (responseWithKey.formData.countryCode) {
            updatedUserInfo.countryCode = responseWithKey.formData.countryCode;
          }
          
          // Persist userInfo changes to Firebase
          await updateUserProgress(userSession.shortcode, { userInfo: updatedUserInfo });
        } catch (error) {
          console.error(`Failed to persist userInfo to Firebase for ${responseWithKey.type}:`, error);
        }
      }
      
      // For membership_entry screens, also persist userInfo changes to Firebase
      if (responseWithKey.type === 'membership_entry' && responseWithKey.formData) {
        try {
          const currentUserInfo = userSession.userInfo || {};
          const updatedUserInfo = { ...currentUserInfo };
          
          // Update all provided fields
          if (responseWithKey.formData.name) {
            updatedUserInfo.name = responseWithKey.formData.name;
          }
          if (responseWithKey.formData.email) {
            updatedUserInfo.email = responseWithKey.formData.email;
          }
          if (responseWithKey.formData.phone) {
            updatedUserInfo.phone = responseWithKey.formData.phone;
          }
          if (responseWithKey.formData.location) {
            updatedUserInfo.location = responseWithKey.formData.location;
          }
          if (responseWithKey.formData.profession) {
            updatedUserInfo.profession = responseWithKey.formData.profession;
          }
          
          // Persist userInfo changes to Firebase
          await updateUserProgress(userSession.shortcode, { userInfo: updatedUserInfo });
        } catch (error) {
          console.error(`Failed to persist userInfo to Firebase for ${responseWithKey.type}:`, error);
        }
      }
      
      maybeSendConversionEvents({
        shortcode: userSession.shortcode,
        itemData: currentCheckpointData,
        itemType: 'checkpoint',
        response: responseWithKey,
        trigger: 'complete'
      });
      
      // For form-like checkpoints, don't pass selectedOption for conditional navigation
      // since they should follow their configured nextScreen directly
      // Commitment uses selectedOption (1=Yes, 0=No) for branching
      const userResponseForNavigation = (responseWithKey.type === 'email_capture' || 
                                         responseWithKey.type === 'phone_verification' || 
                                         responseWithKey.type === 'membership_entry' ||
                                         responseWithKey.type === 'payment' ||
                                         responseWithKey.type === 'payment_membership' ||
                                         responseWithKey.type === 'marketing_landing_page' ||
                                         responseWithKey.type === 'goals' ||
                                         responseWithKey.type === 'speedbumps' ||
                                         responseWithKey.type === 'which_are_you' ||
                                         responseWithKey.type === 'first_name') 
        ? null 
        : response.selectedOption;
      
      // Navigate to next screen with user response for conditional logic
      await handleNext(currentCheckpointData.key, userResponseForNavigation);
      
    } catch (err) {
      console.error('Error saving checkpoint response:', err);
      setError('Failed to save response');
      throw err; // Re-throw so checkpoint screen can handle it
    }
  };

  // Handle journal entry submission
  const handleJournalEntry = async (journalData) => {
    try {
      setError('');
      
      if (!userSession?.shortcode) {
        throw new Error('User session not found');
      }
      
      // Save journal entry and calculate metrics
      const result = await saveJournalEntry(userSession.shortcode, journalData);
      
      // Update local user session state with calculated metrics
      setUserSession(prev => {
        const updatedSession = {
          ...prev,
          journalEntries: [...(prev.journalEntries || []), result.journalEntry],
          calculatedMetrics: {
            ...prev.calculatedMetrics,
            mentalResilience: result.mentalResilience,
            lastMentalResilienceCalculation: Date.now(),
            ...(result.optimalPace && {
              optimalPace: result.optimalPace,
              lastOptimalPaceCalculation: Date.now()
            })
          }
        };
        // Save updated session to browser storage
        saveUserSessionToStorage(updatedSession);
        return updatedSession;
      });
      
      const currentData = currentScreenData || currentCheckpointData;
      if (currentData) {
        if (currentCheckpointData) {
          maybeSendConversionEvents({
            shortcode: userSession.shortcode,
            itemData: currentCheckpointData,
            itemType: 'checkpoint',
            response: { type: 'journal_entry' },
            trigger: 'complete'
          });
        }

        await handleNext(currentData.key);
      }
      
    } catch (error) {
      console.error('Error handling journal entry:', error);
      setError('Failed to save journal entry. Please try again.');
    }
  };

  // Handle Safe Sprinting data submission
  const handleSafeSprintingData = async (safeSprintingMetrics, currentScreenKey) => {
    if (!userSession) return;
    
    try {
      console.log('FunnelContainer: Saving Safe Sprinting metrics:', safeSprintingMetrics);
      
      // Update user progress with Safe Sprinting metrics
      const updates = {
        calculatedMetrics: {
          ...userSession.calculatedMetrics,
          safeSprinting: {
            ...safeSprintingMetrics,
            timestamp: Date.now()
          }
        }
      };
      
      await updateUserProgress(userSession.shortcode, updates);
      
      // Update local session state
      setUserSession(prev => {
        const updatedSession = {
          ...prev,
          calculatedMetrics: {
            ...prev.calculatedMetrics,
            safeSprinting: {
              ...safeSprintingMetrics,
              timestamp: Date.now()
            }
          }
        };
        // Save updated session to browser storage
        saveUserSessionToStorage(updatedSession);
        return updatedSession;
      });
      
      console.log('FunnelContainer: Safe Sprinting metrics saved successfully');
      
      // Advance to next screen
      await handleNext(currentScreenKey);
      
    } catch (error) {
      console.error('Error handling Safe Sprinting data:', error);
      setError('Failed to save Safe Sprinting data. Please try again.');
    }
  };

  // Initialize session on mount or when params change
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Update screen data when screenKey changes
  useEffect(() => {
    if (userSession) {
      // Determine the actual screen key (might be in shortcode parameter for direct access)
      let actualScreenKey = screenKey;
      
      // If we have a shortcode that's actually a screenKey (not 6-char alphanumeric), use it
      if (shortcode && !screenKey) {
        const isLikelyShortcode = /^[A-Z0-9]{6}$/.test(shortcode);
        if (!isLikelyShortcode) {
          actualScreenKey = shortcode;
        }
      }
      
      if (actualScreenKey) {
        loadScreenData(actualScreenKey);
      }
    }
  }, [screenKey, shortcode, userSession]);

  // Save current path to storage whenever location changes
  useEffect(() => {
    if (location.pathname && location.pathname.startsWith('/optimalpace')) {
      saveCurrentPathToStorage(location.pathname);
    }
  }, [location.pathname]);

  // Fire LinkedIn Insight Tag on every funnel screen/checkpoint load (URL path change)
  useEffect(() => {
    if (location.pathname && location.pathname.startsWith('/optimalpace') && typeof window.lintrk === 'function') {
      window.lintrk('track');
    }
  }, [location.pathname]);

  // Fire ad conversions configured to send on load/view
  useEffect(() => {
    if (!userSession?.shortcode) {
      console.log('[Conversion] load-trigger skipped: no userSession.shortcode yet');
      return;
    }

    const itemData = currentScreenData || currentCheckpointData;
    if (!itemData?.key) {
      console.log('[Conversion] load-trigger skipped: no current screen/checkpoint data yet');
      return;
    }

    if (!itemData.conversionConfig?.enabled) {
      console.log(
        `[Conversion] load-trigger skipped: conversions not enabled on ${currentScreenData ? 'screen' : 'checkpoint'} "${itemData.key}". Stored conversionConfig:`,
        itemData.conversionConfig ?? '(field is missing on this document)'
      );
      return;
    }

    if (itemData.conversionConfig.trigger !== 'load') {
      console.log(
        `[Conversion] load-trigger skipped: "${itemData.key}" trigger is "${itemData.conversionConfig.trigger}", not "load"`
      );
      return;
    }

    let actualScreenKey = screenKey;
    if (shortcode && !screenKey) {
      const isLikelyShortcode = /^[A-Z0-9]{6}$/.test(shortcode);
      if (!isLikelyShortcode) {
        actualScreenKey = shortcode;
      }
    }

    // Only fire when the displayed item matches the URL (avoids preload races)
    if (!actualScreenKey || itemData.key !== actualScreenKey) {
      console.log(
        `[Conversion] load-trigger waiting: displayed item "${itemData.key}" does not match URL key "${actualScreenKey || '(none)'}" yet`
      );
      return;
    }

    const fireKey = `${userSession.shortcode}:${itemData.key}:load`;
    if (lastLoadConversionKey.current === fireKey) {
      console.log(`[Conversion] load-trigger skipped: already fired for "${itemData.key}" this session`);
      return;
    }
    lastLoadConversionKey.current = fireKey;

    maybeSendConversionEvents({
      shortcode: userSession.shortcode,
      itemData,
      itemType: currentScreenData ? 'screen' : 'checkpoint',
      trigger: 'load'
    });
  }, [userSession?.shortcode, currentScreenData, currentCheckpointData, screenKey, shortcode]);

  // Update progress when session or current screen changes
  useEffect(() => {
    const updateProgress = async () => {
      if (userSession && (currentScreenData || currentCheckpointData)) {
        try {
          const currentKey = currentScreenData?.key || currentCheckpointData?.key;

          // Skip full funnel scan for ad-entry first checkpoints (expensive getAll*)
          if (AD_ENTRY_CHECKPOINT_KEYS.has(currentKey)) {
            setProgress(0);
            return;
          }

          const { progress: sectionProgress } = await calculateSectionProgress(
            currentKey, 
            userSession.completedScreens || [],
            userSession
          );
          
          setProgress(sectionProgress);
        } catch (error) {
          console.error('Error calculating progress:', error);
          // Fallback to simple calculation
          const completed = userSession.completedScreens?.length || 0;
          const fallbackProgress = Math.min(100, (completed / 10) * 100);
          setProgress(fallbackProgress);
        }
      }
    };
    
    updateProgress();
  }, [userSession, currentScreenData, currentCheckpointData]);

  // Show loading state only when we have nothing to paint yet
  if (isLoading && !currentScreenData && !currentCheckpointData) {
    return (
      <AppContainer>
        <ProgressBar currentProgress={0} showText={false} />
        <div style={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          fontSize: '18px',
          color: '#3B515D'
        }}>
          Loading ...
        </div>
      </AppContainer>
    );
  }

  // Show error state`
  if (error) {
    return (
      <AppContainer>
        <ProgressBar currentProgress={progress} showText={false} />
        <div style={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center'
        }}>
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/insatiable-e0f18.firebasestorage.app/o/screen%202%20ring.svg?alt=media&token=5b73be3c-978d-4c6b-8f8b-992708facbe5"
            alt="Error illustration"
            style={{
              width: '120px',
              height: 'auto',
              marginBottom: '24px'
            }}
          />
          <h2 style={{ color: '#3B515D', marginBottom: '16px' }}>Oh Goodness</h2>
          <p style={{ color: '#121616', marginBottom: '24px' }}>
            That wasn't supposed to happen. Please excuse the mistake. Tap the button below and let's try it again.
          </p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg, #405C73 0%, #435F60 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </AppContainer>
    );
  }

  const currentData = currentScreenData || currentCheckpointData;
  
  return (
    <AppContainer>
      <ProgressBar 
        currentProgress={progress}
        showText={false}
      />
      
      <ScreenRenderer
        screenData={currentScreenData}
        checkpointData={currentCheckpointData}
        onNext={handleNext}
        onResponse={handleCheckpointResponse}
        onJournalEntry={handleJournalEntry}
        onSafeSprintingData={handleSafeSprintingData}
        userProgress={userSession}
        isLoading={isLoading}
      />
    </AppContainer>
  );
};

export default FunnelContainer; 